import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { ListScaffold } from "@/components/layout/ListScaffold";
import {
  Badge,
  PageHeader,
  Select,
  Table,
  TableWrap,
  TD,
  TH,
  THead,
  TR,
} from "@/components/ui";
import { useApi } from "@/lib/api";
import { dateTime, shortId } from "@/lib/format";

const TABLES = [
  "",
  "users",
  "cafe",
  "review",
  "report",
  "cafe_proposal",
  "fact_correction",
  "user_feedback",
  "job",
];

/**
 * Every operator action, newest first.
 *
 * This is where "who did that" is answered, and it is the ONLY place: an
 * operator has no row in `users`, so `moderation_log.actor_id` and the
 * `resolved_by` columns on the queues are all null by design. The audit log
 * keys on the admin pool's `sub` instead, and every mutation in the API
 * writes one row here before it answers.
 */
export function AuditScreen() {
  const api = useApi();
  const [table, setTable] = useState("");

  const entries = useQuery({
    queryKey: ["admin", "audit", table],
    queryFn: async () => {
      const { data, error } = await api.GET("/admin/audit", {
        params: {
          query: { targetTable: table || undefined, limit: 50 },
        },
      });
      if (error) throw error;
      return data;
    },
  });

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        eyebrow="System"
        title="Audit log"
        description="Written by the API before it answers. An operator is a Cognito sub, not an account — this is the only record of who acted."
      />
      <ListScaffold
        filters={
          <Select value={table} onChange={(e) => setTable(e.target.value)}>
            {TABLES.map((name) => (
              <option key={name} value={name}>
                {name === "" ? "Everything" : name}
              </option>
            ))}
          </Select>
        }
        isLoading={entries.isLoading}
        isError={entries.isError}
        onRetry={() => void entries.refetch()}
        isEmpty={(entries.data?.items.length ?? 0) === 0}
        emptyTitle="Nothing recorded yet"
        emptyHint="The log fills the first time somebody changes something from this console."
      >
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>When</TH>
                <TH>Operator</TH>
                <TH>Action</TH>
                <TH>Target</TH>
                <TH>Id</TH>
              </TR>
            </THead>
            <tbody>
              {entries.data?.items.map((entry) => (
                <TR key={entry.id}>
                  <TD mono muted>
                    {dateTime(entry.createdAt)}
                  </TD>
                  <TD mono>{entry.adminEmail ?? shortId(entry.adminSub)}</TD>
                  <TD>
                    <Badge tone="accent">{entry.action}</Badge>
                  </TD>
                  <TD muted mono>
                    {entry.targetTable}
                  </TD>
                  <TD mono muted>
                    {shortId(entry.targetId)}
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      </ListScaffold>
    </div>
  );
}
